import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import {
  fetchEmptyQuerySettings,
  saveEmptyQuerySettings,
  fetchEmptyQueryArtifact,
  searchEmptyQueryCategories,
  resolveEmptyQueryCategories,
  clearPinnedCategoryObjects,
  selectEmptyQuerySettings,
  selectEmptyQuerySettingsScope,
  selectEmptyQuerySettingsStatus,
  selectEmptyQuerySettingsError,
  selectEmptyQueryArtifact,
  selectEmptyQueryArtifactScope,
  selectPinnedCategoryObjects,
} from 'slices/EmptyQuerySlice';
import {
  searchProducts,
  resolveProducts,
  clearResolvedProducts,
  selectResolvedProducts,
} from 'slices/ProductSelectorSlice';
import SettingsForm from './components/SettingsForm';
import styles from './EmptyQuery.module.scss';

const EMPTY_PAYLOAD = {
  enabled: true,
  product_limit: 16,
  manual_term_suggestions: [],
  excluded_term_suggestions: [],
  manual_words: [],
  excluded_words: [],
  manual_category_ids: [],
  excluded_category_ids: [],
  pinned_product_ids: [],
  excluded_product_ids: [],
  auto_fill_products: true,
  auto_fill_term_suggestions: true,
  auto_fill_categories: true,
  auto_fill_words: true,
};

const normalizeStringList = (list) => (Array.isArray(list) ? list : []);

const normalizeIdList = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((id) => {
    const value = `${id}`;
    return /^\d+$/.test(value) ? Number(value) : value;
  });
};

export const buildEditablePayload = (settings = EMPTY_PAYLOAD) => ({
  // Empty-query merchandising is an always-on search surface. Keep the field
  // in the transport payload for backward compatibility, but do not expose a
  // second enable/disable mode in the editor.
  enabled: true,
  product_limit: Math.min(Math.max(Number(settings.product_limit) || 16, 1), 100),
  manual_term_suggestions: normalizeStringList(settings.manual_term_suggestions),
  excluded_term_suggestions: normalizeStringList(settings.excluded_term_suggestions),
  manual_words: normalizeStringList(settings.manual_words),
  excluded_words: normalizeStringList(settings.excluded_words),
  manual_category_ids: normalizeIdList(settings.manual_category_ids),
  excluded_category_ids: normalizeIdList(settings.excluded_category_ids),
  pinned_product_ids: normalizeIdList(settings.pinned_product_ids),
  excluded_product_ids: normalizeIdList(settings.excluded_product_ids),
  auto_fill_products: settings.auto_fill_products !== false,
  auto_fill_term_suggestions: settings.auto_fill_term_suggestions !== false,
  auto_fill_categories: settings.auto_fill_categories !== false,
  auto_fill_words: settings.auto_fill_words !== false,
});

const isEqualArray = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

export const isEditablePayloadDirty = (draft, saved) => (
  draft.product_limit !== saved.product_limit
  || draft.auto_fill_products !== saved.auto_fill_products
  || draft.auto_fill_term_suggestions !== saved.auto_fill_term_suggestions
  || draft.auto_fill_categories !== saved.auto_fill_categories
  || draft.auto_fill_words !== saved.auto_fill_words
  || !isEqualArray(draft.manual_term_suggestions, saved.manual_term_suggestions)
  || !isEqualArray(draft.excluded_term_suggestions, saved.excluded_term_suggestions)
  || !isEqualArray(draft.manual_words, saved.manual_words)
  || !isEqualArray(draft.excluded_words, saved.excluded_words)
  || !isEqualArray(draft.manual_category_ids, saved.manual_category_ids)
  || !isEqualArray(draft.excluded_category_ids, saved.excluded_category_ids)
  || !isEqualArray(draft.pinned_product_ids, saved.pinned_product_ids)
  || !isEqualArray(draft.excluded_product_ids, saved.excluded_product_ids)
);

const buildUnresolvedCategoryStub = (id) => ({
  id,
  name: `Category #${id}`,
  path: '',
  image_url: '',
  productsCount: 0,
  unresolved: true,
});

const buildUnresolvedProductStub = (id) => ({
  id,
  name: `Product #${id}`,
  thumbnail: '',
  vendor: '',
  vendorCode: '',
  price: 0,
  oldprice: 0,
  available: false,
  unresolved: true,
});

const mergeKnownObjects = (current = [], incoming = []) => {
  const byId = new Map(current.map((item) => [`${item.id}`, item]));

  incoming.forEach((item) => {
    if (!item || item.id == null) return;
    byId.set(`${item.id}`, item);
  });

  return Array.from(byId.values());
};

const EmptyQueryPage = () => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const { capabilities = {} } = useShellAppContext();

  const settings = useSelector(selectEmptyQuerySettings);
  const settingsScope = useSelector(selectEmptyQuerySettingsScope);
  const settingsStatus = useSelector(selectEmptyQuerySettingsStatus);
  const settingsError = useSelector(selectEmptyQuerySettingsError);
  const artifact = useSelector(selectEmptyQueryArtifact);
  const artifactScope = useSelector(selectEmptyQueryArtifactScope);
  const visualSearchEnabled = useSelector(
    state => state.settings?.visualSearchEnabled === true,
  );

  const pinnedCategoryObjects = useSelector(selectPinnedCategoryObjects);
  const resolvedProducts = useSelector(selectResolvedProducts);

  const [hasSaveError, setHasSaveError] = useState(false);
  const [scope, setScope] = useState('default');
  const [savedPayload, setSavedPayload] = useState(EMPTY_PAYLOAD);
  const [draftPayload, setDraftPayload] = useState(EMPTY_PAYLOAD);
  const [knownCategoryObjects, setKnownCategoryObjects] = useState([]);
  const [knownProductObjects, setKnownProductObjects] = useState([]);
  const showScopeTabs = capabilities.emptyQueryVisualV1 === true && visualSearchEnabled;

  useEffect(() => {
    setHasSaveError(false);
    setKnownCategoryObjects([]);
    setKnownProductObjects([]);
    dispatch(clearPinnedCategoryObjects());
    dispatch(clearResolvedProducts());
    dispatch(fetchEmptyQuerySettings(scope));
    dispatch(fetchEmptyQueryArtifact(scope));
  }, [dispatch, scope]);

  useEffect(() => {
    if (settingsStatus === 'saving' || settingsScope !== scope) return;
    const normalized = buildEditablePayload(settings);
    setSavedPayload(normalized);
    setDraftPayload(normalized);
  }, [scope, settings, settingsScope, settingsStatus]);

  const activeArtifact = artifactScope === scope ? artifact : null;

  useEffect(() => {
    const ids = Array.from(new Set([
      ...draftPayload.manual_category_ids,
      ...draftPayload.excluded_category_ids,
      ...(activeArtifact?.category_preview || []).map(category => category.id),
    ]));
    if (ids.length > 0) {
      dispatch(resolveEmptyQueryCategories(ids));
    } else {
      dispatch(clearPinnedCategoryObjects());
    }
  }, [
    dispatch,
    activeArtifact?.category_preview,
    draftPayload.excluded_category_ids,
    draftPayload.manual_category_ids,
  ]);

  useEffect(() => {
    const ids = Array.from(new Set([
      ...draftPayload.pinned_product_ids,
      ...draftPayload.excluded_product_ids,
    ]));
    if (ids.length > 0) {
      dispatch(resolveProducts(ids));
    } else {
      dispatch(clearResolvedProducts());
    }
  }, [
    dispatch,
    draftPayload.excluded_product_ids,
    draftPayload.pinned_product_ids,
  ]);

  useEffect(() => {
    setKnownCategoryObjects((current) => mergeKnownObjects(current, pinnedCategoryObjects));
  }, [pinnedCategoryObjects]);

  useEffect(() => {
    setKnownProductObjects((current) => mergeKnownObjects(current, resolvedProducts));
  }, [resolvedProducts]);

  const hasChanges = useMemo(
    () => isEditablePayloadDirty(draftPayload, savedPayload),
    [draftPayload, savedPayload],
  );

  const draftCategoryObjects = useMemo(() => {
    const knownById = new Map(knownCategoryObjects.map((category) => [`${category.id}`, category]));
    (pinnedCategoryObjects || []).forEach((category) => {
      knownById.set(`${category.id}`, category);
    });

    return draftPayload.manual_category_ids.map((id) => (
      knownById.get(`${id}`) || buildUnresolvedCategoryStub(id)
    ));
  }, [draftPayload.manual_category_ids, knownCategoryObjects, pinnedCategoryObjects]);

  const draftProductObjects = useMemo(() => {
    const knownById = new Map(knownProductObjects.map((product) => [`${product.id}`, product]));
    (resolvedProducts || []).forEach((product) => {
      knownById.set(`${product.id}`, product);
    });

    return draftPayload.pinned_product_ids.map((id) => (
      knownById.get(`${id}`) || buildUnresolvedProductStub(id)
    ));
  }, [draftPayload.pinned_product_ids, knownProductObjects, resolvedProducts]);

  const updateDraft = useCallback((partial) => {
    setDraftPayload((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleCategoriesChange = useCallback((categories) => {
    setKnownCategoryObjects((current) => mergeKnownObjects(current, categories));
    updateDraft({ manual_category_ids: normalizeIdList(categories.map(category => category.id)) });
  }, [updateDraft]);

  const handleProductsChange = useCallback((products) => {
    setKnownProductObjects((current) => mergeKnownObjects(current, products));
    updateDraft({ pinned_product_ids: normalizeIdList(products.map(product => product.id)) });
  }, [updateDraft]);

  const handleSave = useCallback(async () => {
    setHasSaveError(false);
    const result = await dispatch(saveEmptyQuerySettings({ settings: draftPayload, scope }));

    if (saveEmptyQuerySettings.fulfilled.match(result)) {
      const normalized = buildEditablePayload(result.payload || draftPayload);
      setSavedPayload(normalized);
      setDraftPayload(normalized);
      return;
    }

    setHasSaveError(true);
  }, [dispatch, draftPayload, scope]);

  const handleDiscard = useCallback(() => {
    setHasSaveError(false);
    setDraftPayload(savedPayload);
  }, [savedPayload]);

  const handleSearchCategories = useCallback(async (query) => {
    const result = await dispatch(searchEmptyQueryCategories(query));
    if (searchEmptyQueryCategories.fulfilled.match(result)) {
      return result.payload || [];
    }
    return [];
  }, [dispatch]);

  const handleSearchProducts = useCallback(async (query, limit = 8) => {
    const result = await dispatch(searchProducts({ query, limit }));
    if (searchProducts.fulfilled.match(result)) {
      return result.payload || [];
    }
    return [];
  }, [dispatch]);

  const handleScopeChange = useCallback((event, nextScope) => {
    if (!nextScope || nextScope === scope || hasChanges) return;
    setScope(nextScope);
  }, [hasChanges, scope]);

  if (settingsStatus === 'loading') {
    return (
      <SearchFeaturePageShell pageId="customizationEmptyQuery" isLoading />
    );
  }

  if (settingsStatus === 'error') {
    return (
      <SearchFeaturePageShell
        pageId="customizationEmptyQuery"
        error={settingsError || t('empty.query.form.load.error')}
        onRetry={() => dispatch(fetchEmptyQuerySettings(scope))}
      />
    );
  }

  return (
    <SearchFeaturePageShell
      pageId="customizationEmptyQuery"
      description={t('empty.query.page.subtitle')}
      actions={(
        <SaveActionBtnGrp
          isFetching={settingsStatus === 'saving'}
          buttons={[
            { text: t('common.save'), action: handleSave, color: 'primary' },
            {
              text: t('customization.filters.actions.discard'),
              action: handleDiscard,
              color: 'secondary',
            },
          ]}
          disabled={!hasChanges}
        />
      )}
    >
      <div className={styles.page}>
        {showScopeTabs && (
          <div className={styles.scopeSelector} data-testid="empty-query-scope-selector">
            <p className={styles.scopeLabel}>{t('empty.query.scope.label')}</p>
            <Tabs
              value={scope}
              onChange={handleScopeChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={styles.scopeTabs}
              aria-label={t('empty.query.scope.label')}
            >
              <Tab
                id="empty-query-scope-default-tab"
                aria-controls="empty-query-scope-default-panel"
                value="default"
                label={t('empty.query.scope.default')}
                className={styles.scopeTab}
                disabled={hasChanges && scope !== 'default'}
                data-testid="empty-query-scope-default"
              />
              <Tab
                id="empty-query-scope-visual-tab"
                aria-controls="empty-query-scope-visual-panel"
                value="visual"
                label={t('empty.query.scope.visual')}
                className={styles.scopeTab}
                disabled={hasChanges && scope !== 'visual'}
                data-testid="empty-query-scope-visual"
              />
            </Tabs>
            <p className={styles.scopeDescription}>
              {t(`empty.query.scope.${scope}.description`)}
            </p>
          </div>
        )}

        <div
          {...(showScopeTabs ? {
            role: 'tabpanel',
            id: `empty-query-scope-${scope}-panel`,
            'aria-labelledby': `empty-query-scope-${scope}-tab`,
          } : {})}
        >
          {hasSaveError && (
            <div className={`${styles.notice} ${styles.noticeError}`} role="alert">
              {t('empty.query.form.error')}
            </div>
          )}

          <SettingsForm
            enabled={draftPayload.enabled}
            productLimit={draftPayload.product_limit}
            terms={draftPayload.manual_term_suggestions}
            excludedTerms={draftPayload.excluded_term_suggestions}
            words={draftPayload.manual_words}
            excludedWords={draftPayload.excluded_words}
            categories={draftCategoryObjects}
            excludedCategoryIds={draftPayload.excluded_category_ids}
            products={draftProductObjects}
            excludedProductIds={draftPayload.excluded_product_ids}
            autoFillProducts={draftPayload.auto_fill_products}
            autoFillTerms={draftPayload.auto_fill_term_suggestions}
            autoFillCategories={draftPayload.auto_fill_categories}
            autoFillWords={draftPayload.auto_fill_words}
            hasChanges={hasChanges}
            isSaving={settingsStatus === 'saving'}
            onSave={handleSave}
            onDiscard={handleDiscard}
            onTermsChange={(terms) => updateDraft({ manual_term_suggestions: terms })}
            onExcludedTermsChange={(terms) => updateDraft({ excluded_term_suggestions: terms })}
            onWordsChange={(words) => updateDraft({ manual_words: words })}
            onExcludedWordsChange={(words) => updateDraft({ excluded_words: words })}
            onCategoriesChange={handleCategoriesChange}
            onExcludedCategoryIdsChange={(ids) => updateDraft({ excluded_category_ids: normalizeIdList(ids) })}
            onProductsChange={handleProductsChange}
            onExcludedProductIdsChange={(ids) => updateDraft({ excluded_product_ids: normalizeIdList(ids) })}
            onAutoFillProductsChange={(value) => updateDraft({ auto_fill_products: value })}
            onAutoFillTermsChange={(value) => updateDraft({ auto_fill_term_suggestions: value })}
            onAutoFillCategoriesChange={(value) => updateDraft({ auto_fill_categories: value })}
            onAutoFillWordsChange={(value) => updateDraft({ auto_fill_words: value })}
            generatedTerms={activeArtifact?.term_preview || []}
            generatedWords={activeArtifact?.words_preview || []}
            generatedCategories={activeArtifact?.category_preview || []}
            generatedProducts={activeArtifact?.product_preview || []}
            knownCategories={knownCategoryObjects}
            knownProducts={knownProductObjects}
            onSearchCategories={handleSearchCategories}
            onSearchProducts={handleSearchProducts}
            onProductLimitChange={(productLimit) => updateDraft({ product_limit: productLimit })}
          />
        </div>
      </div>
    </SearchFeaturePageShell>
  );
};

export default EmptyQueryPage;
